import { useEffect, useLayoutEffect } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useLocation } from '@tanstack/react-router';
import { SparkleIcon, HeadCircuitIcon } from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import { useAgentPanel } from '@/context/agent-panel.context';
import { useAgentContainer } from '@/context/agent-container.context';
import { SidebarMenuButton } from '@/components/ui/sidebar';
import { isMac } from '@/utils/platform';
import paths from '@/paths';
// Demo mode hides the sidebar Help menu (where the AI-assistant trigger lives),
// so surface a floating button to open Nami. Delete with the demo framework.
import { isDemoMode } from '@/demo/core/demo-mode';
import { HELP_MENU_ORDER, HelpMenuSlot } from './HelpMenu';
import { AgentContainer } from './AgentContainer';

const PANEL_WIDTH = '400px';

/**
 * Mounted once at the authenticated-layout level so the AI sidebar item is
 * always visible. Each page can opt into AI assistance by calling
 * `useAgentModule()`, which registers a `moduleConfig`. While no module is
 * registered the trigger renders as a disabled sidebar item with a tooltip
 * explaining where the assistant is available.
 */
export function FloatingAgentPanel() {
  const gt = useGT();
  const { isOpen, toggle, close } = useAgentPanel();
  const { moduleConfig } = useAgentContainer();
  const { pathname } = useLocation();

  const hasModule = moduleConfig !== null;
  // AP Invoices Demo (delete me) — the build canvas opens Nami on its own (and
  // keeps it open), so the floating opener is redundant there.
  const onApWorkflowCanvas = pathname === paths.authenticated.demoApWorkflow;
  const panelActive = hasModule && isOpen;
  const width = panelActive ? PANEL_WIDTH : '0px';

  // Sync the layout CSS variable that `SidebarInset` reads for its right
  // padding. Use `useLayoutEffect` so the DOM update happens synchronously
  // after commit (before paint), avoiding a one-frame flash where the inset
  // padding lags the panel open/close state. Reset to '0px' on unmount so a
  // remount on a fresh page never inherits a stale width.
  useLayoutEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--agent-panel-width', width);
    return () => {
      root.style.setProperty('--agent-panel-width', '0px');
    };
  }, [width]);

  // If the user opens the panel on a page that registers an agent module and
  // then navigates somewhere that doesn't, drop the open state so we don't
  // leave a phantom panel slot stuck in the layout.
  useEffect(() => {
    if (!hasModule && isOpen) close();
  }, [hasModule, isOpen, close]);

  // Cmd/Ctrl+Shift+A toggles the AI panel. Only active when the current page
  // has registered an agent module.
  useHotkeys(
    'mod+shift+a',
    () => {
      if (hasModule) toggle();
    },
    { preventDefault: true, enabled: hasModule },
    [hasModule, toggle],
  );

  const shortcutLabel = isMac() ? '⌘⇧A' : 'Ctrl+Shift+A';

  const enabledTooltip = gt('Open AI assistant ({shortcut})', {
    context:
      'tooltip on the collapsed sidebar AI assistant menu item, includes keyboard shortcut',
    shortcut: shortcutLabel,
  });
  const disabledTooltip = gt('AI assistant — available on workflow pages', {
    context:
      'tooltip explaining that the AI assistant is only enabled on certain pages',
  });

  return (
    <>
      {/* Sidebar trigger — always rendered so users can discover the AI
          assistant from anywhere. Disabled when no page has registered an
          agent module. Stays visible while the panel is open so clicking it
          acts as a close affordance. */}
      <HelpMenuSlot order={HELP_MENU_ORDER.ai}>
        <SidebarMenuButton
          onClick={hasModule ? () => toggle() : undefined}
          disabled={!hasModule}
          isActive={panelActive}
          className="cursor-pointer"
          aria-label={gt('Open AI assistant', {
            context: 'sidebar help menu button aria label for AI assistant',
          })}
          tooltip={hasModule ? enabledTooltip : disabledTooltip}
        >
          <SparkleIcon weight="fill" className="text-purple-600" />
          {/* Trailing label/shortcut block — hidden in icon-mode so the
              SidebarMenuButton can center the icon inside its 32×32 shell. */}
          <span className="flex w-full items-center justify-between gap-2 group-data-[collapsible=icon]:hidden">
            <T context="sidebar help menu item label for opening the AI assistant">
              AI assistant
            </T>
            <kbd className="rounded border border-sidebar-border/60 bg-sidebar-accent/40 px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
              {shortcutLabel}
            </kbd>
          </span>
        </SidebarMenuButton>
      </HelpMenuSlot>

      {/* Fixed side panel — only mounted when a module is registered, so we
          never show an empty container. */}
      {panelActive && (
        <div
          // Marker so a slide-in's onInteractOutside (and the shared Sheet
          // primitive) can tell clicks into the chat panel apart from true
          // outside clicks, and not dismiss the sheet.
          data-agent-panel=""
          // z-[1310] sits above slide-in sheets (z-[1300]) and their dimming
          // overlay so the chat panel stays clear and clickable while a slide-in
          // is open, while staying below true modals/popovers (z-[1500]).
          className="bg-background fixed right-0 top-0 z-[1310] h-screen w-[400px] border-l shadow-xl"
        >
          <AgentContainer className="flex h-full w-full flex-col overflow-hidden pb-6" />
        </div>
      )}

      {/* Demo mode hides the sidebar Help menu (which holds the AI-assistant
          trigger), so render an icon-only floating button to open/close Nami.
          Shown only when a page has registered an agent module. Sits just left
          of the chat panel when it's open via `--agent-panel-width`. Hidden on
          the AP workflow build canvas, which drives Nami itself. */}
      {isDemoMode() && hasModule && !onApWorkflowCanvas && (
        <button
          type="button"
          onClick={() => toggle()}
          aria-label={panelActive ? gt('Close AI assistant') : enabledTooltip}
          title={panelActive ? gt('Close AI assistant') : enabledTooltip}
          style={{ right: 'calc(var(--agent-panel-width, 0px) + 1.5rem)' }}
          className="fixed bottom-6 z-[1320] flex size-12 items-center justify-center rounded-full border bg-background shadow-lg transition-[right] duration-200 ease-in-out hover:bg-muted"
        >
          <HeadCircuitIcon
            size={26}
            weight="duotone"
            className="text-violet-600"
          />
        </button>
      )}
    </>
  );
}
